import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface PipelineRow {
  readonly pipelineId: string;
  readonly pipelineCode: string;
  readonly pipelineName: string;
  readonly status: string;
  readonly lastRunStatus: string;
  readonly schedule: string;
}

const STATUSES = ['', 'ACTIVE', 'PAUSED', 'DISABLED'];
const RUN_STATUSES = ['', 'SUCCESS', 'RUNNING', 'FAILED', 'SKIPPED'];

/**
 * GR L26.03 — ELT Pipelines (`/dwh/pipelines`). Operational view of the ELT
 * pipeline registry: each `elt_pipeline_definitions` row with its enablement
 * status, schedule and the outcome of its most recent run, so data engineers
 * can spot failed/paused loads. Filterable by pipeline status and last-run outcome.
 */
export function GrPipelinesPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<PipelineRow[]>([]);
  const [status, setStatus] = useState('');
  const [runStatus, setRunStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (runStatus) params.set('lastRunStatus', runStatus);
    const qs = params.toString();
    apiRequest<{ data: PipelineRow[] }>(`/v1/dwh/pipelines${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, runStatus]);

  return (
    <main aria-label="pipelines">
      <h1>{t('gr26.pipelines.title')}</h1>
      <div className="filters">
        <label>
          {t('gr26.pipelines.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr26.pipelines.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr26.pipelines.lastRun')}
          <select aria-label="filter-run-status" value={runStatus} onChange={(e) => setRunStatus(e.target.value)}>
            {RUN_STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr26.pipelines.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr26.pipelines.loading')}</p> : (
        <>
          <p aria-label="pipeline-count">{t('gr26.pipelines.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr26.pipelines.empty')}</p> : (
            <table aria-label="pipeline-table">
              <thead><tr><th>{t('gr26.pipelines.code')}</th><th>{t('gr26.pipelines.name')}</th><th>{t('gr26.pipelines.status')}</th><th>{t('gr26.pipelines.lastRunCol')}</th><th>{t('gr26.pipelines.schedule')}</th></tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.pipelineId}>
                    <td>{p.pipelineCode}</td><td>{p.pipelineName}</td><td>{p.status}</td>
                    <td>{p.lastRunStatus}</td><td>{p.schedule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
